import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Snackbar,
  Alert,
  Autocomplete,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import { userServices } from '../../services/userServices';
import { teamServices } from '../../services/teamServices';
import { assistanceTeamServices } from '../../services/assistanceTeamServices';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../css/Form.css';
import '../../css/TeamMembers.css';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../common/ConfirmationDialog';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import TableSkeleton from '../common/TableSkeleton';
import PageHeader from '../ui/PageHeader';

const TeamMembers = () => {
  const [userData, setUserData] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Route state
  const {
    teamId: stateTeamId,
    teamName: stateTeamName,
    isAssistanceTeam = false,
    parentTeamId = null,
    parentTeamName = ''
  } = location.state || {};

  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState(null);
  const [teamMemberData, setTeamMemberData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogForMember, setOpenDialogForMember] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Fetch Team Members (either Standard Team or Assistance Team)
  const fetchTeamMembers = useCallback(() => {
    if (!teamId) return;
    setLoading(true);

    if (isAssistanceTeam) {
      assistanceTeamServices.getAssistanceTeamMembers(teamId)
        .then((response) => {
          setTeamMemberData(response || []);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setSnackbarMessage('Error fetching assistance team members.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    } else {
      userServices.getTeamMembersData(teamId)
        .then((response) => {
          setTeamMemberData(response || []);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setSnackbarMessage('Error fetching team members.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }
  }, [teamId, isAssistanceTeam]);

  // Fetch Eligible Users to add
  const fetchAvailableUsersList = useCallback(() => {
    if (isAssistanceTeam) {
      if (parentTeamId) {
        assistanceTeamServices.getEligibleMembers(parentTeamId, teamId)
          .then((response) => {
            setUserData(response || []);
          })
          .catch((err) => {
            console.error("Error fetching eligible assistance team members:", err);
          });
      }
    } else {
      userServices.getMemberUserForTeam()
        .then((response) => {
          setUserData(response || []);
        })
        .catch((err) => {
          console.error("Error fetching users:", err);
        });
    }
  }, [isAssistanceTeam, parentTeamId, teamId]);

  useEffect(() => {
    if (stateTeamId) {
      setTeamId(stateTeamId);
      setTeamName(stateTeamName || '');
    }
  }, [stateTeamId, stateTeamName]);

  useEffect(() => {
    if (teamId) {
      fetchTeamMembers();
      fetchAvailableUsersList();
    }
  }, [teamId, fetchTeamMembers, fetchAvailableUsersList]);

  const handleUserChange = (event, value) => {
    setAssignedUserIds(value || []);
  };

  const handleSave = () => {
    if (assignedUserIds.length === 0) {
      setSnackbarMessage('Please select at least one team member.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const userIds = assignedUserIds.map(user => user.id);
    const formData = {
      id: teamId,
      userIds
    };

    if (isAssistanceTeam) {
      assistanceTeamServices.addMemberUserToAssistanceTeam(formData)
        .then(() => {
          fetchTeamMembers();
          fetchAvailableUsersList();
          setSnackbarMessage('Member(s) added to assistance team successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setAssignedUserIds([]);
        })
        .catch((err) => {
          const msg = err.response?.data?.message || err.response?.data || 'Error adding member(s) to assistance team';
          setSnackbarMessage(typeof msg === 'string' ? msg : 'Error adding member(s) to assistance team');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    } else {
      teamServices.addMemberUserToTeam(formData)
        .then(() => {
          fetchTeamMembers();
          fetchAvailableUsersList();
          setSnackbarMessage('User added to team successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setAssignedUserIds([]);
        })
        .catch((err) => {
          const msg = err.response?.data?.message || err.response?.data || 'Error adding user to team';
          setSnackbarMessage(typeof msg === 'string' ? msg : 'Error adding user to team');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }
  };

  const handleDeleteMember = () => {
    if (!selectedMember) return;

    if (isAssistanceTeam) {
      assistanceTeamServices.removeMemberFromAssistanceTeam(selectedMember)
        .then(() => {
          setTeamMemberData((prevMembers) => prevMembers.filter(member => member.id !== selectedMember));
          fetchAvailableUsersList();
          setSnackbarMessage('Member removed from assistance team');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        })
        .catch(() => {
          setSnackbarMessage('Error removing member from assistance team');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    } else {
      userServices.removeTeamMembersFromTeam(selectedMember)
        .then(() => {
          setTeamMemberData((prevMembers) => prevMembers.filter(member => member.id !== selectedMember));
          fetchAvailableUsersList();
          setSnackbarMessage('Member removed from team');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        })
        .catch(() => {
          setSnackbarMessage('Error removing member from team');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }
    handleCloseDialogForRemoveMember();
  };

  const handleDeleteTeam = (tid, event) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setTeamToDelete(tid);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTeamToDelete(null);
  };

  const handleCloseDialogForRemoveMember = () => {
    setOpenDialogForMember(false);
    setSelectedMember(null);
  };

  const handleDialogForRemoveMember = (userId, event) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setOpenDialogForMember(true);
    setSelectedMember(userId);
  };

  const confirmDelete = () => {
    if (!teamToDelete) return;

    if (isAssistanceTeam) {
      assistanceTeamServices.deleteAssistanceTeamData(teamToDelete)
        .then(() => {
          setSnackbarMessage('Assistance Team deleted successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate('/home/teams');
          }, 1000);
        })
        .catch(() => {
          setSnackbarMessage('Error deleting assistance team');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    } else {
      teamServices.deleteTeamData(teamToDelete)
        .then(() => {
          setSnackbarMessage('Team deleted successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate('/home/teams');
          }, 1000);
        })
        .catch(() => {
          setSnackbarMessage('Error deleting team data');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }
    handleCloseDialog();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <Box className="team-members-container">
      <PageHeader
        title={teamName || "Team Members"}
        left={
          <IconButton onClick={() => navigate('/home/teams')} sx={{ color: 'var(--primary)', ml: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        }
      />

      {/* Action Section */}
      <Paper className="action-card" elevation={0}>
        <Box className="action-card-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              Manage Membership
            </Typography>
            {isAssistanceTeam && (
              <Chip
                label={`Assistance Team • Parent: ${parentTeamName || 'Team'}`}
                size="small"
                sx={{
                  backgroundColor: '#e6f4f5',
                  color: '#045864',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary">
            {isAssistanceTeam
              ? "Add members from the parent team to this assistance team (members can belong to only one assistance team under this parent team)."
              : "Add new members to the team or delete the team entirely."}
          </Typography>
        </Box>

        <Box className="action-bar-layout">
          <Box className="autocomplete-wrapper">
            <Autocomplete
              multiple
              value={assignedUserIds}
              onChange={handleUserChange}
              options={userData}
              getOptionLabel={(option) => [option.firstName, option.middleName, option.lastName].filter(Boolean).join(' ')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose people..."
                  label={isAssistanceTeam ? "Available Parent Team Members" : "Available Users"}
                  variant="outlined"
                  fullWidth
                />
              )}
              noOptionsText={isAssistanceTeam ? 'No available parent team members' : 'No users found'}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            className="form-button-team-member"
            startIcon={<PersonAddIcon />}
            disableElevation
          >
            Add Member
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => handleDeleteTeam(teamId, e)}
            className="form-button-team-member-delete"
            startIcon={<DeleteIcon />}
          >
            {isAssistanceTeam ? "Delete Assistance Team" : "Delete Team"}
          </Button>
        </Box>
      </Paper>

      {/* Members Table Section */}
      <Box className="member-list-card">
        {loading ? (
          <Box sx={{ p: 1 }}>
            <TableSkeleton rows={5} columns={2} title="Active Members" />
          </Box>
        ) : teamMemberData.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead className="modern-table-head">
                <TableRow>
                  <TableCell sx={{ width: '80%' }}>Name</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMemberData.map((member) => (
                  <TableRow key={member.id} className="modern-table-row">
                    <TableCell className="modern-table-cell">
                      <Box className="member-name-cell">
                        <Box className="member-avatar">
                          {getInitials(member.firstName, member.lastName)}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {`${member.firstName || ''} ${member.middleName || ''} ${member.lastName || ''}`.trim()}
                          </Typography>
                          {member.emailId && (
                            <Typography variant="caption" color="textSecondary">
                              {member.emailId}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell className="modern-table-cell">
                      <IconButton
                        className="remove-member-btn"
                        onClick={(e) => handleDialogForRemoveMember(member.id, e)}
                        size="small"
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box className="empty-state-container">
            <PeopleIcon className="empty-state-icon" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              No Team Members
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {isAssistanceTeam
                ? "Use the selector above to assign members from the parent team to this assistance team."
                : "Use the selector above to add members to this team."}
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={confirmDelete}
        title={isAssistanceTeam ? "Delete Assistance Team" : "Delete Team"}
        message={isAssistanceTeam ? "Are you sure you want to delete this assistance team?" : "Are you sure you want to delete this team?"}
        messageSecond={isAssistanceTeam ? "*Members will remain in the parent team." : "*Associated team members will also be removed."}
        confirmLabel="Delete"
        confirmColor="error"
      />

      <ConfirmationDialog
        open={openDialogForMember}
        onClose={handleCloseDialogForRemoveMember}
        onConfirm={handleDeleteMember}
        title="Remove Member"
        message={isAssistanceTeam ? "Are you sure you want to remove this member from the assistance team?" : "Are you sure you want to remove this member from the team?"}
        confirmLabel="Remove"
        confirmColor="error"
      />
    </Box>
  );
};

export default TeamMembers;
